import { Alert, Button, Divider, Grid, Paper, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { OrderedQuestion } from '../../../pages/motive-survey';
import QuestionContainer from './question-container';
import { AnswerType } from '../survey.types';

interface Props {
  selectedValue: number[];
  allQuestionsAnswered: boolean;
  submissionError: string | null;
  missingQuestionIndexes: number[];
  questionsApplyVersion: OrderedQuestion[];
  questionsImportanceVersion: OrderedQuestion[];
  selectionChangeHandlerGenerator(
    shuffledIdx: number,
    realIdx: number,
    realType: string,
  ): (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSendButton(): void;
}

const CHUNK_SIZE = 5;

const SurveyQuestionnaire: React.FC<Props> = ({
  selectedValue,
  allQuestionsAnswered,
  submissionError,
  missingQuestionIndexes,
  selectionChangeHandlerGenerator,
  handleSendButton,
  questionsApplyVersion,
  questionsImportanceVersion,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [appliesChunks, setAppliesChunks] = useState<OrderedQuestion[][]>([]);
  const [importantChunks, setImportantChunks] = useState<OrderedQuestion[][]>([]);
  const [appliesIndex, setAppliesIndex] = useState(0);
  const [importantIndex, setImportantIndex] = useState(0);
  const [appliesDone, setAppliesDone] = useState(false);

  useEffect(() => {
    setAppliesChunks(_.chunk(questionsApplyVersion, CHUNK_SIZE));
    setImportantChunks(_.chunk(questionsImportanceVersion, CHUNK_SIZE));
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const totalPages = (questionsApplyVersion.length + questionsImportanceVersion.length) / CHUNK_SIZE;
  const currentPage = appliesDone ? appliesIndex + importantIndex + 2 : appliesIndex + importantIndex + 1;
  const isLastPage = appliesDone && importantIndex === importantChunks.length - 1;
  const visibleQuestions = appliesDone ? importantChunks[importantIndex] : appliesChunks[appliesIndex];

  const handleNext = () => {
    if (!appliesDone) {
      if (appliesIndex < appliesChunks.length - 1) {
        setAppliesIndex((prev) => prev + 1);
      } else {
        setAppliesDone(true);
      }
    } else {
      if (importantIndex < importantChunks.length - 1) {
        setImportantIndex((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (!appliesDone) {
      if (appliesIndex > 0) {
        setAppliesIndex((prev) => prev - 1);
      }
    } else {
      if (importantIndex > 0) {
        setImportantIndex((prev) => prev - 1);
      } else {
        setAppliesDone(false);
      }
    }
  };

  const canGoBack = appliesIndex > 0 || (appliesDone && importantIndex >= 0);

  return (
    <Grid container direction="row" spacing={2}>
      <Grid size={12}>
        <Typography variant="h4" gutterBottom>{t('questionnaireTitle')}</Typography>
      </Grid>
      <Grid size={12}>
        <Divider className={classes.divider} />
      </Grid>
      <Grid size={12}>
        <Paper className={classes.infoPaper} elevation={0}>
          {!appliesDone ? (
            <>
              <Typography variant="body1" gutterBottom>{t('questionnaireInfo1')}</Typography>
              <Typography variant="body1" gutterBottom>{t('questionnaireInfo2')}</Typography>
              <Typography variant="body1" gutterBottom>{t('questionnaireInfo3')}</Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>{t('questionnaireInfo4')}</Typography>
              <Typography variant="body1" gutterBottom>{t('questionnaireInfo5')}</Typography>
            </>
          )}
        </Paper>
      </Grid>

      <Grid size={12}>
        {submissionError && (
          <Alert severity="error" className={classes.errorAlert}>
            {submissionError}
          </Alert>
        )}
      </Grid>

      <Grid size={12}>
        {appliesChunks.length > 0 && importantChunks.length > 0 && (
          <QuestionContainer
            questions={visibleQuestions}
            selectionChangeHandlerGenerator={selectionChangeHandlerGenerator}
            selectedValue={selectedValue}
            answerType={appliesDone ? AnswerType.IMPORTANT : AnswerType.APPLIES}
            missingQuestionIndexes={missingQuestionIndexes}
          />
        )}
      </Grid>

      {/* Pagination and navigation */}
      <Grid size={12} className={classes.navigationRow}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid size={6}>
            <Typography variant="body2" color="textSecondary">
              {t('questionnairePagination', { page: currentPage, pagesCount: totalPages })}
            </Typography>
          </Grid>
          <Grid size={6} className={classes.navButtons}>
            {canGoBack && (
              <Button variant="outlined" color="primary" onClick={handleBack}>
                {t('prev')}
              </Button>
            )}
            <Button
              variant={!isLastPage ? 'contained' : 'text'}
              color="primary"
              onClick={handleNext}
              disabled={isLastPage}
            >
              {!isLastPage ? t('continue') : t('done')}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid size={12} className={classes.submitRow}>
        <Button variant="contained" color="primary" size="large" disabled={!allQuestionsAnswered} onClick={handleSendButton}>
          {t('sendSurvey')}
        </Button>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles()((theme) => ({
  infoPaper: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
    border: `2px solid ${theme.palette.secondary.main}`,
    borderRadius: theme.spacing(1),
    minHeight: '100px',
  },
  divider: {
    borderColor: theme.palette.secondary.main,
  },
  navigationRow: {
    marginTop: theme.spacing(4),
  },
  errorAlert: {
    marginTop: theme.spacing(2),
  },
  navButtons: {
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  submitRow: {
    textAlign: 'right',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
}));

export default React.memo(SurveyQuestionnaire);
